import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, CloudRain, Loader2, AlertTriangle, CheckCircle2, Users, Undo2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { CreditDecisionRow } from "../components/CreditDecisionRow";
import { CreditsToDecide } from "../components/CreditsToDecide";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminRainOut() {
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("Rain");
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [undone, setUndone] = useState(null);
  const [undoDate, setUndoDate] = useState("");
  const [busy, setBusy] = useState(false);
  // player_id -> "credit" | "later" | "none"
  const [decisions, setDecisions] = useState({});
  const [skipNotes, setSkipNotes] = useState({}); // player_id -> why no credit
  const [worklistKey, setWorklistKey] = useState(0);

  // One child can train in more than one programme on the same day, so the
  // flattened list repeats them. Count and submit each player once, or the
  // confirmation total stops matching what is actually issued.
  const seen = new Set();
  const candidates = (preview?.terms || []).flatMap((t) =>
    (t.affected_paid_players || []).filter((p) => {
      const key = `${t.term_id}:${p.player_id || p.registration_id}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }));
  const open = candidates.filter((p) => !p.already_credited && !p.unlinked);
  const unlinked = candidates.filter((p) => p.unlinked);
  const decisionOf = (p) => decisions[p.player_id] || "credit";
  const creditIds = open.filter((p) => decisionOf(p) === "credit").map((p) => p.player_id);
  const laterIds = open
    .filter((p) => decisionOf(p) === "later" && !creditIds.includes(p.player_id))
    .map((p) => p.player_id);
  const noneIds = open.filter((p) => decisionOf(p) === "none").map((p) => p.player_id);
  const includedTotal = open
    .filter((p) => decisionOf(p) === "credit")
    .reduce((s, p) => s + Number(p.credit_amount || 0), 0);
  const alreadyCredited = candidates.filter((p) => p.already_credited).length;
  const rowsFor = (t) => candidates.filter((p) => (t.affected_paid_players || []).includes(p));

  const undo = async (target) => {
    if (!target) return;
    setBusy(true);
    try {
      const pre = await axios.post(`${API}/academy/admin/restore-session-day`, {
        date: target, preview: true,
      }, { withCredentials: true });
      const n = (pre.data.terms || []).reduce((s, t) => s + (t.credits_to_reverse || 0), 0);
      const amt = (pre.data.terms || []).reduce((s, t) => s + (t.amount_to_reverse || 0), 0);
      if ((pre.data.terms_affected || 0) === 0) {
        toast.warning("That date isn't cancelled anywhere");
        return;
      }
      if (!window.confirm(`Put ${target} back on in ${pre.data.terms_affected} programme(s) and reverse ${n} credit(s) worth $${amt.toFixed(2)}?`)) return;
      const r = await axios.post(`${API}/academy/admin/restore-session-day`, {
        date: target,
      }, { withCredentials: true });
      setUndone(r.data);
      setResult(null);
      setPreview(null);
      setWorklistKey((k) => k + 1); // parked players move back onto the worklist
      toast.success(`${target} restored — ${r.data.credits_reversed} credit(s) reversed`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not restore that day");
    } finally {
      setBusy(false);
    }
  };

  const runPreview = async (forDate) => {
    const target = forDate || date;
    if (!target) { toast.error("Pick the date that was called off"); return; }
    setBusy(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/academy/admin/cancel-session-day`, {
        date: target, reason: reason.trim() || "Session cancelled", preview: true,
      }, { withCredentials: true });
      setPreview(r.data);
      // Start from what we already know: last time's call, or their availability
      const start = {};
      const notes = {};
      (r.data.terms || []).forEach((t) => (t.affected_paid_players || []).forEach((p) => {
        if (p.already_credited) return;
        if (!p.player_id) return;
        // A player parked as "skip for now" is exactly who this revisit is for:
        // start them on Credit so finishing the day is one tap, not a re-park.
        if (p.payment_unverified) {
          // Invoiced but nothing collected — never credit one of these by
          // default, even if they were parked for a second look.
          start[p.player_id] = "none";
          notes[p.player_id] = p.prior_reason || "Invoiced but nothing collected";
        } else if (p.prior_decision === "no_credit") {
          start[p.player_id] = "none";
          if (p.prior_reason) notes[p.player_id] = p.prior_reason;
        } else if (p.rsvp_attending === false) {
          start[p.player_id] = "none";
          if (p.rsvp_note) notes[p.player_id] = p.rsvp_note;
        }
      }));
      setDecisions(start);
      setSkipNotes(notes);
      if ((r.data.terms_affected || 0) === 0) {
        toast.warning(`No programme trains on ${r.data.day_name} inside an open term`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not check that date");
    } finally {
      setBusy(false);
    }
  };

  // Every programme on this date is already off, so a second visit is only
  // about the students still waiting on a credit.
  const dayAlreadyOff = (preview?.terms || []).length > 0
    && (preview.terms || []).every((t) => t.already_cancelled);

  const openFromWorklist = (d) => {
    setDate(d);
    setUndone(null);
    runPreview(d);
  };

  const commit = async (issueCredits) => {
    const n = creditIds.length;
    // The day may already be off — coming back to finish the remaining
    // students must not read, or behave, like cancelling it all over again.
    const alreadyOff = (preview?.terms || []).length > 0
      && (preview.terms || []).every((t) => t.already_cancelled);
    const where = `${preview.day_name} ${date}`;
    const msg = issueCredits
      ? (alreadyOff
          ? `Issue ${n} credit(s) $${includedTotal.toFixed(2)} for ${where}?`
            + `\n\nThe day is already called off — nothing else changes.`
          : `Cancel ${where} across ${preview.terms_affected} programme(s) and credit ${n} player(s) $${includedTotal.toFixed(2)}?`)
        + (laterIds.length ? `\n\n${laterIds.length} player(s) parked as "skip for now" — they'll stay on your to-decide list.` : "")
      : (alreadyOff
          ? `Leave ${where} as it is and issue no credits?`
          : `Cancel ${where} across ${preview.terms_affected} programme(s) WITHOUT issuing any credits?`);
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/academy/admin/cancel-session-day`, {
        date,
        reason: reason.trim() || "Session cancelled",
        issue_credits: issueCredits,
        include_player_ids: issueCredits ? [...new Set(creditIds)] : [],
        skip_for_now_player_ids: issueCredits ? [...new Set(laterIds)] : [],
        skip_notes: skipNotes,
      }, { withCredentials: true });
      setResult(r.data);
      setPreview(null);
      setWorklistKey((k) => k + 1);
      toast.success(issueCredits
        ? `${r.data.credits_issued} credit(s) issued — $${r.data.total_credit}`
        : alreadyOff
          ? `${where} left as it is`
          : `${r.data.day_name} ${date} cancelled with no credits`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel that day");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-950 to-black text-slate-100 px-4 py-5 sm:p-8">
      <div className="max-w-4xl mx-auto">
        <Link to="/admin/credits" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-5" data-testid="rainout-back">
          <ArrowLeft className="w-4 h-4" /> Back to Credits
        </Link>

        <h1 className="text-2xl sm:text-4xl font-bold text-white flex items-center gap-3">
          <CloudRain className="w-8 h-8 text-sky-400" />
          Call off a session day
        </h1>
        <p className="text-slate-400 mt-2 mb-6 text-sm">
          One date, one reason. Every programme training that weekday is called off, and you decide
          player by player who gets a credit — including coming back later to finish anyone you parked.
        </p>

        <Card className="bg-slate-900/50 border-slate-700 p-4 sm:p-6 mb-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-400">Date called off</label>
              <Input
                type="date"
                value={date}
                onChange={(e) => { setDate(e.target.value); setPreview(null); setResult(null); }}
                className="bg-slate-950 border-slate-700 mt-1 h-11"
                data-testid="rainout-date"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-400">Reason parents will see</label>
              <Input
                placeholder="Rain"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="bg-slate-950 border-slate-700 mt-1 h-11"
                data-testid="rainout-reason"
              />
            </div>
          </div>
          <Button
            onClick={() => runPreview()}
            disabled={busy || !date}
            className="mt-4 w-full sm:w-auto bg-sky-600 hover:bg-sky-500 h-11"
            data-testid="rainout-preview-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
            Check who's affected
          </Button>
        </Card>

        <CreditsToDecide onOpenDate={openFromWorklist} refreshKey={worklistKey} />

        {preview && (
          <Card className="bg-slate-900/50 border-sky-700/50 p-4 sm:p-6 mb-6" data-testid="rainout-preview">
            <h2 className="text-lg font-semibold text-white mb-1">
              {preview.day_name} {preview.date}
            </h2>
            <p className="text-sm text-slate-400 mb-4">
              {preview.terms_affected} programme(s) train that day · {candidates.length} paid player(s)
              found · crediting <span className="text-teal-300 font-mono">{creditIds.length}</span> of them
              (<span className="text-teal-300 font-mono">${includedTotal.toFixed(2)}</span>)
              {laterIds.length > 0 && (
                <> · <span className="text-sky-300">{laterIds.length} to decide later</span></>
              )}
              {noneIds.length > 0 && (
                <> · <span className="text-amber-300">{noneIds.length} no credit</span></>
              )}
              {alreadyCredited > 0 && (
                <> · <span className="text-emerald-300">{alreadyCredited} already credited</span></>
              )}
              {unlinked.length > 0 && (
                <> · <span className="text-rose-300" data-testid="rainout-unlinked-count">
                  {unlinked.length} paid but not linked to a child record</span></>
              )}
              {preview.unpaid_offset_count > 0 && (
                <> · <span className="text-slate-300">{preview.unpaid_offset_count}{" "}
                  {preview.unpaid_offset_count === 1 ? "hasn't" : "haven't"} paid yet — this
                  session is simply left off their bill</span></>
              )}
            </p>
            <p className="text-xs text-slate-500 mb-3">
              Three choices per player. <span className="text-sky-300">Skip for now</span> if you still
              need to check their records — nothing is issued and they come back up on your to-decide
              list. <span className="text-amber-300">No credit</span> when you&apos;ve decided they
              aren&apos;t owed one, with the reason kept on the record. Anyone who marked themselves
              unavailable starts on &ldquo;No credit&rdquo;. So does anyone whose fee was invoiced but
              never collected — a credit refunds money actually taken.
            </p>
            <p className="text-xs text-slate-500 mb-3">
              Amounts differ between children in the same programme when a parent holds a discount:
              each credit refunds what that child was actually charged for the session, not the list
              price. Tap a figure to see the workings.
            </p>

            <div className="space-y-3">
              {(preview.terms || []).map((t) => {
                const rows = rowsFor(t);
                const toDecide = rows.filter((p) => !p.already_credited && !p.unlinked);
                const termTotal = toDecide.reduce((s, p) => s + Number(p.credit_amount || 0), 0);
                const termCredited = rows.filter((p) => p.already_credited).length;
                const termUnlinked = rows.filter((p) => p.unlinked).length;
                return (
                <div key={t.term_id} className="bg-slate-800/40 rounded-lg p-3" data-testid={`rainout-term-${t.term_id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-white text-sm font-medium">{t.term_name}</p>
                    <p className="text-xs text-slate-400">
                      {toDecide.length} to
                      decide · <span className="font-mono text-teal-300">${termTotal.toFixed(2)}</span> owed
                      {termCredited > 0 && ` · ${termCredited} already credited`}
                      {termUnlinked > 0 && ` · ${termUnlinked} not linked`}
                      {t.unpaid_offset_count > 0 && ` · ${t.unpaid_offset_count} not billed`}
                    </p>
                  </div>
                  {t.already_cancelled && (
                    <p className="text-[11px] text-amber-300 mt-1">Already cancelled for this date</p>
                  )}
                  {(t.affected_paid_players || []).length > 0 && (
                    <div className="mt-2 space-y-1.5">
                      {rows.map((p) => (p.unlinked ? (
                        <div key={`unlinked-${p.registration_id}`}
                          className="bg-slate-900/70 border border-rose-700/60 rounded px-2 py-1.5"
                          data-testid={`rainout-unlinked-${p.registration_id}`}>
                          <p className="text-xs text-rose-200">
                            {p.player_name}
                            {p.parent_name ? ` · ${p.parent_name}` : ""} ·{" "}
                            <span className="font-mono">${p.credit_amount}</span> owed,
                            but this paid registration has no child record attached — link the child on
                            the registration and the credit can then be issued.
                            <span className="block text-[10px] text-rose-300/70 mt-0.5">
                              Registration {p.registration_id}
                            </span>
                          </p>
                        </div>
                      ) : (
                        <CreditDecisionRow
                          key={p.player_id}
                          player={p}
                          decision={decisions[p.player_id] || "credit"}
                          note={skipNotes[p.player_id] || ""}
                          onDecide={(d) => setDecisions((s) => ({ ...s, [p.player_id]: d }))}
                          onNote={(v) => setSkipNotes((s) => ({ ...s, [p.player_id]: v }))}
                        />
                      )))}
                    </div>
                  )}
                  {(t.unpaid_offset_rows || []).length > 0 && (
                    <div className="mt-2 border-t border-slate-700/60 pt-2"
                      data-testid={`rainout-notbilled-${t.term_id}`}>
                      <p className="text-[11px] text-slate-400 mb-1">
                        Nothing to credit — {t.unpaid_offset_rows.length === 1
                          ? "this child hasn't"
                          : `these ${t.unpaid_offset_rows.length} children haven't`}{" "}
                        paid yet, so the session is left off their bill:
                      </p>
                      <div className="space-y-0.5">
                        {t.unpaid_offset_rows.map((u, i) => (
                          <p key={`${u.registration_id}-${i}`} className="text-[11px] text-slate-300"
                            data-testid={`rainout-notbilled-row-${u.player_id || i}`}>
                            {u.player_name}
                            {u.parent_name && <span className="text-slate-500"> · {u.parent_name}</span>}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                );
              })}
            </div>

            <div className="flex flex-col sm:flex-row gap-2 mt-5">
              <Button
                onClick={() => commit(true)}
                disabled={busy || (dayAlreadyOff && creditIds.length === 0)}
                className="bg-teal-600 hover:bg-teal-500 h-11 flex-1"
                data-testid="rainout-commit-credits"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                {dayAlreadyOff
                  ? (creditIds.length === 0
                      ? "Nothing selected to credit"
                      : `Issue ${creditIds.length} credit(s)`)
                  : `Cancel day & issue ${creditIds.length} credit(s)`}
              </Button>
              <Button
                onClick={() => commit(false)}
                disabled={busy}
                variant="outline"
                className="border-slate-600 text-slate-300 h-11"
                data-testid="rainout-commit-nocredits"
              >
                {dayAlreadyOff ? "Leave as it is" : "Cancel without credits"}
              </Button>
            </div>
            {dayAlreadyOff && (
              <p className="mt-3 text-[11px] text-amber-300" data-testid="rainout-already-off-note">
                This day is already called off. Nothing is cancelled again — you are only issuing
                the credits still outstanding, and parents were told at the time.
              </p>
            )}
            <p className="text-[11px] text-slate-500 mt-3 flex items-start gap-1">
              <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
              Parents are emailed and notified straight away. Credits go only to players who have
              already paid for that session — and land on any invoice still outstanding. Anyone still
              to pay simply sees the session deducted from their fee, so nobody is charged twice.
            </p>
          </Card>
        )}

        {result && (
          <Card className="bg-emerald-500/10 border-emerald-600/40 p-4 sm:p-6" data-testid="rainout-result">
            <h2 className="text-lg font-semibold text-emerald-200 mb-2">
              {result.day_name} {result.date}
              {result.already_cancelled ? " — credits issued" : ` cancelled — ${result.reason}`}
            </h2>
            <p className="text-sm text-emerald-100/90">
              {result.already_cancelled
                ? `${result.credits_issued} credit(s) issued`
                : `${result.terms_affected} programme(s) updated · ${result.credits_issued} credit(s) issued`} ·
              <span className="font-mono"> ${result.total_credit}</span>
              {result.already_cancelled
                ? " · the day stays called off and parents were told at the time"
                : ` · ${result.parents_notified || 0} parent(s) being notified`}
              {result.credits_skipped ? ` · ${result.credits_skipped} no credit` : ""}
              {result.credits_pending ? ` · ${result.credits_pending} still to decide` : ""}
              {result.credits_unlinked ? ` · ${result.credits_unlinked} need a child record linked` : ""}
            </p>
            <div className="mt-3 space-y-1">
              {(result.terms || []).map((t) => (
                <p key={t.term_id} className="text-xs text-emerald-100/80" data-testid={`rainout-result-term-${t.term_id}`}>
                  {t.term_name}: {t.credits_issued} credit(s) · ${t.total_credit}
                </p>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 items-center mt-4">
              <Link to="/admin/credits" className="text-sm text-emerald-200 underline" data-testid="rainout-result-to-credits">
                See the credits ledger →
              </Link>
              <Button
                variant="outline"
                size="sm"
                className="border-amber-500/50 text-amber-200 h-9"
                onClick={() => undo(result.date)}
                disabled={busy}
                data-testid="rainout-undo-btn"
              >
                <Undo2 className="w-4 h-4 mr-1" /> Undo this rain-out
              </Button>
            </div>
          </Card>
        )}

        {undone && (
          <Card className="bg-amber-500/10 border-amber-600/40 p-4 sm:p-6 mt-4" data-testid="rainout-undo-result">
            <h2 className="text-lg font-semibold text-amber-200 mb-1">
              {undone.day_name} {undone.date} is back on
            </h2>
            <p className="text-sm text-amber-100/90">
              Restored in {undone.terms_affected} programme(s) · {undone.credits_reversed} credit(s) reversed
              (<span className="font-mono">${undone.amount_reversed}</span>) ·{" "}
              {undone.parents_notified || 0} parent(s) told
            </p>
          </Card>
        )}

        <Card className="bg-slate-900/40 border-slate-700 p-4 sm:p-6 mt-6">
          <h3 className="text-sm font-semibold text-white mb-1">Called a day off by mistake?</h3>
          <p className="text-xs text-slate-400 mb-3">
            Put the date back and pull the credits straight back out. Parents get told it's back on.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="date"
              value={undoDate}
              onChange={(e) => setUndoDate(e.target.value)}
              className="bg-slate-950 border-slate-700 h-11 sm:max-w-[200px]"
              data-testid="rainout-undo-date"
            />
            <Button
              variant="outline"
              onClick={() => undo(undoDate)}
              disabled={busy || !undoDate}
              className="border-amber-500/50 text-amber-200 h-11"
              data-testid="rainout-undo-date-btn"
            >
              <Undo2 className="w-4 h-4 mr-1" /> Restore that day
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
